import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Search, X, History, Check, ExternalLink, AlertTriangle, ArrowRightLeft } from "lucide-react";
import RigaMaterialiCalcolatiPanel from "@/pages/RigaMaterialiCalcolatiPanel";

/**
 * Pannello ANALISI PREZZO per una singola riga computo (FASE B).
 *
 * Regole cardine:
 * - Nessuna analisi viene mai applicata automaticamente.
 * - I suggerimenti vengono caricati solo su richiesta esplicita ("Trova analisi").
 * - L'utente conferma manualmente con "Usa questa analisi".
 * - Lo storico è preservato: cambiare analisi disattiva la precedente.
 */
export default function RigaAnalisiPanel({ riga, canEdit = true }) {
  const [attiva, setAttiva] = useState(null);      // mapping attivo
  const [loading, setLoading] = useState(true);
  const [suggOpen, setSuggOpen] = useState(false);
  const [suggList, setSuggList] = useState([]);
  const [suggLoading, setSuggLoading] = useState(false);
  const [confirmSug, setConfirmSug] = useState(null); // suggerimento da confermare
  const [noteConfirma, setNoteConfirma] = useState("");
  const [storicoOpen, setStoricoOpen] = useState(false);
  const [storico, setStorico] = useState([]);
  const [storicoLoading, setStoricoLoading] = useState(false);
  const [changeMaterialiInfo, setChangeMaterialiInfo] = useState(null); // dialog cambio analisi con materiali esistenti
  const [bridgeOpen, setBridgeOpen] = useState(false);
  const [bridgePlan, setBridgePlan] = useState(null);
  const [bridgeLoading, setBridgeLoading] = useState(false);
  const [bridgeApplying, setBridgeApplying] = useState(false);

  const rigaId = riga?.id;

  const load = async () => {
    if (!rigaId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/righe/${rigaId}/analisi`);
      setAttiva(data);
    } catch {
      setAttiva(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rigaId]);

  const findSuggest = async () => {
    setSuggLoading(true);
    setSuggOpen(true);
    try {
      const { data } = await api.get(`/righe/${rigaId}/analisi/suggerimenti?top=5`);
      setSuggList(data || []);
      if (!data || data.length === 0) toast.info("Nessun suggerimento trovato nelle banche dati");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSuggLoading(false);
    }
  };

  const doApply = async () => {
    if (!confirmSug) return;
    // Se sto cambiando analisi (attiva è != scelta) e ho materiali calcolati attivi,
    // chiedo cosa fare col vecchio set.
    if (attiva && attiva.analisi_id !== confirmSug.analisi_id) {
      try {
        const { data } = await api.get(`/righe/${rigaId}/materiali-calcolati`);
        if ((data?.materiali || []).some(m => m.is_active)) {
          setChangeMaterialiInfo({
            oldCount: data.materiali.filter(m => m.is_active).length,
            hasOverride: data.materiali.some(m => m.is_active && m.is_manual_override),
          });
          return; // apre dialog dedicato
        }
      } catch { /* fallback: procedi come normale */ }
    }
    await _doApplyReal(null);
  };

  const _doApplyReal = async (materialiAction /* null|'mantieni'|'rigenera' */) => {
    try {
      await api.post(`/righe/${rigaId}/analisi`, {
        analisi_id: confirmSug.analisi_id,
        match_score: confirmSug.score,
        match_livello: confirmSug.livello,
        note: noteConfirma?.trim() || null,
      });
      toast.success("Analisi collegata alla riga");
      // Se richiesto: rigenera i materiali con il nuovo mapping (override mantenuti).
      if (materialiAction === "rigenera") {
        try {
          await api.post(`/righe/${rigaId}/materiali-calcolati/genera`, {
            disattiva_precedenti: true,
            mantieni_override: true,
          });
          toast.success("Risorse ricalcolate dalla nuova analisi");
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore rigenerazione risorse");
        }
      }
      setConfirmSug(null);
      setNoteConfirma("");
      setSuggOpen(false);
      setChangeMaterialiInfo(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const doRemove = async () => {
    if (!confirm("Rimuovere l'analisi collegata? Lo storico viene conservato.")) return;
    try {
      await api.delete(`/righe/${rigaId}/analisi`);
      toast.success("Analisi rimossa dalla riga (storico conservato)");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const openStorico = async () => {
    setStoricoOpen(true);
    setStoricoLoading(true);
    try {
      const { data } = await api.get(`/righe/${rigaId}/analisi/storico`);
      setStorico(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setStoricoLoading(false);
    }
  };

  const openBridge = async () => {
    setBridgeOpen(true);
    setBridgeLoading(true);
    setBridgePlan(null);
    try {
      const { data } = await api.get(`/righe/${rigaId}/analisi/sviluppa-in-previsti/preview`);
      setBridgePlan(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      setBridgeOpen(false);
    } finally {
      setBridgeLoading(false);
    }
  };

  const confirmBridge = async () => {
    setBridgeApplying(true);
    try {
      const { data } = await api.post(`/righe/${rigaId}/analisi/sviluppa-in-previsti`);
      const tot = (data.materiali_creati || 0) + (data.materiali_aggiornati || 0)
                + (data.manodopera_creati || 0) + (data.manodopera_aggiornati || 0);
      toast.success(tot > 0
        ? `Sviluppo eseguito: ${data.materiali_creati} materiali creati, ${data.materiali_aggiornati} aggiornati, ${data.manodopera_creati} MDO create, ${data.manodopera_aggiornati} aggiornate`
        : "Nessuna modifica: i previsti erano già allineati.");
      setBridgeOpen(false);
      setBridgePlan(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBridgeApplying(false);
    }
  };

  if (loading) return <div className="text-slate-500 text-sm p-4">Caricamento analisi…</div>;

  return (
    <div className="space-y-3" data-testid="riga-analisi-panel">
      {!attiva ? (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold mb-1">Analisi prezzo</div>
                <div className="text-sm text-slate-500">Nessuna analisi collegata. Cerca nei prezzari le voci più affini a questa lavorazione.</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={openStorico} data-testid="analisi-storico-btn"><History className="h-4 w-4 mr-1" /> Storico</Button>
                {canEdit && (
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" onClick={findSuggest} data-testid="analisi-trova-btn">
                    <Search className="h-4 w-4 mr-1" /> Trova analisi
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-xs uppercase tracking-widest text-emerald-600 font-semibold mb-1">Analisi prezzo collegata</div>
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 break-words">
                  {attiva.analisi_descrizione}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                  {attiva.banca_nome && <Badge variant="outline" data-testid="analisi-attiva-banca">{attiva.banca_nome}{attiva.banca_anno ? ` · ${attiva.banca_anno}` : ""}</Badge>}
                  {attiva.analisi_codice_voce && <Badge variant="outline">cod. {attiva.analisi_codice_voce}</Badge>}
                  {attiva.analisi_um && <Badge variant="outline">UM {attiva.analisi_um}</Badge>}
                  {attiva.match_score != null && (
                    <Badge className={
                      attiva.match_livello === "ALTO" ? "bg-emerald-500/90 text-slate-950" :
                      attiva.match_livello === "MEDIO" ? "bg-amber-500/90 text-slate-950" :
                      attiva.match_livello === "BASSO" ? "bg-rose-400/90 text-slate-950" :
                      "bg-slate-400/90 text-slate-950"
                    } data-testid="analisi-attiva-livello">
                      {attiva.match_livello} {Number(attiva.match_score).toFixed(0)}%
                    </Badge>
                  )}
                </div>
                {attiva.note && <div className="mt-2 text-xs text-slate-500 italic">Note: {attiva.note}</div>}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={openStorico} data-testid="analisi-storico-btn"><History className="h-4 w-4 mr-1" /> Storico</Button>
                {canEdit && (
                  <>
                    <Button variant="outline" size="sm" onClick={openBridge}
                      data-testid="analisi-sviluppa-previsti-btn">
                      <ArrowRightLeft className="h-4 w-4 mr-1" /> Sviluppa in Materiali/MDO prevista
                    </Button>
                    <Button variant="outline" size="sm" onClick={findSuggest} data-testid="analisi-cambia-btn">
                      <Search className="h-4 w-4 mr-1" /> Cambia analisi
                    </Button>
                    <Button variant="ghost" size="sm" onClick={doRemove} className="text-rose-600 hover:text-rose-700" data-testid="analisi-rimuovi-btn">
                      <X className="h-4 w-4 mr-1" /> Rimuovi
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog: SUGGERIMENTI */}
      <Dialog open={suggOpen} onOpenChange={setSuggOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="analisi-suggerimenti-dialog">
          <DialogHeader>
            <DialogTitle>Suggerimenti analisi prezzo</DialogTitle>
            <div className="text-xs text-slate-500 mt-1">
              La lista mostra le voci più simili nelle banche dati. Nessun collegamento avviene in automatico: conferma manualmente quella corretta.
            </div>
          </DialogHeader>
          {suggLoading ? (
            <div className="text-slate-500 text-sm py-6 text-center">Ricerca in corso…</div>
          ) : suggList.length === 0 ? (
            <div className="text-slate-500 text-sm py-6 text-center">Nessun risultato — probabilmente nessuna banca dati contiene voci affini.</div>
          ) : (
            <div className="space-y-3">
              {suggList.map((s) => (
                <SuggestionCard key={s.analisi_id} s={s}
                  onUse={() => { setConfirmSug(s); }}
                  disabled={!canEdit} />
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: CONFERMA ASSEGNAZIONE (secondo step obbligatorio) */}
      <Dialog open={!!confirmSug} onOpenChange={(o) => { if (!o) { setConfirmSug(null); setNoteConfirma(""); } }}>
        <DialogContent className="max-w-lg" data-testid="analisi-conferma-dialog">
          <DialogHeader>
            <DialogTitle>Confermi il collegamento?</DialogTitle>
          </DialogHeader>
          {confirmSug && (
            <div className="space-y-3 text-sm">
              <div className="text-slate-600 dark:text-slate-300">Stai per collegare a questa riga la seguente analisi prezzo:</div>
              <div className="p-3 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40">
                <div className="font-semibold">{confirmSug.descrizione}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {confirmSug.banca_nome}{confirmSug.banca_anno ? ` · ${confirmSug.banca_anno}` : ""}
                  {confirmSug.codice_voce ? ` · cod. ${confirmSug.codice_voce}` : ""}
                  {confirmSug.unita_misura ? ` · UM ${confirmSug.unita_misura}` : ""}
                  {confirmSug.score != null ? ` · score ${Number(confirmSug.score).toFixed(0)}%` : ""}
                  {confirmSug.livello ? ` · ${confirmSug.livello}` : ""}
                </div>
              </div>
              {attiva && (
                <div className="text-xs text-amber-600 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  L'analisi attualmente collegata verrà disattivata (rimane nello storico).
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Note (opzionale)</label>
                <Textarea value={noteConfirma} onChange={(e) => setNoteConfirma(e.target.value)}
                  placeholder="Motivo della scelta, riferimento specifico, ecc." rows={2}
                  data-testid="analisi-conferma-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setConfirmSug(null); setNoteConfirma(""); }} data-testid="analisi-conferma-annulla">Annulla</Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" onClick={doApply} data-testid="analisi-conferma-ok">
              <Check className="h-4 w-4 mr-1" /> Conferma e collega
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: STORICO */}
      <Dialog open={storicoOpen} onOpenChange={setStoricoOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="analisi-storico-dialog">
          <DialogHeader>
            <DialogTitle>Storico abbinamenti analisi prezzo</DialogTitle>
          </DialogHeader>
          {storicoLoading ? (
            <div className="text-slate-500 text-sm py-6 text-center">Caricamento…</div>
          ) : storico.length === 0 ? (
            <div className="text-slate-500 text-sm py-6 text-center">Nessun abbinamento registrato per questa riga.</div>
          ) : (
            <div className="space-y-2">
              {storico.map((m) => (
                <div key={m.id} className={`p-3 rounded border ${m.is_active
                  ? "border-emerald-400 bg-emerald-50/60 dark:bg-emerald-900/20"
                  : "border-slate-200 bg-slate-50/60 dark:bg-slate-900/40 opacity-80"}`}
                  data-testid={`analisi-storico-row-${m.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold">{m.analisi_descrizione || "(analisi cancellata)"}</div>
                      <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        {m.banca_nome && <span>{m.banca_nome}{m.banca_anno ? ` · ${m.banca_anno}` : ""}</span>}
                        {m.analisi_codice_voce && <span>cod. {m.analisi_codice_voce}</span>}
                        {m.analisi_um && <span>UM {m.analisi_um}</span>}
                        {m.match_score != null && <span>score {Number(m.match_score).toFixed(0)}%</span>}
                        {m.match_livello && <span>{m.match_livello}</span>}
                      </div>
                      {m.note && <div className="text-xs italic text-slate-500 mt-1">Note: {m.note}</div>}
                      <div className="text-[10px] text-slate-400 mt-1">
                        Confermato: {m.confermato_at ? new Date(m.confermato_at).toLocaleString("it-IT") : "—"}
                      </div>
                    </div>
                    <Badge className={m.is_active ? "bg-emerald-500 text-slate-950" : "bg-slate-300 text-slate-800"}>
                      {m.is_active ? "Attivo" : "Storico"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: CAMBIO ANALISI CON MATERIALI ESISTENTI */}
      <Dialog open={!!changeMaterialiInfo} onOpenChange={(o) => { if (!o) setChangeMaterialiInfo(null); }}>
        <DialogContent className="max-w-lg" data-testid="analisi-cambio-materiali-dialog">
          <DialogHeader><DialogTitle>Materiali calcolati esistenti</DialogTitle></DialogHeader>
          {changeMaterialiInfo && (
            <div className="space-y-3 text-sm">
              <div className="text-slate-600 dark:text-slate-300">
                Questa riga possiede <strong>{changeMaterialiInfo.oldCount}</strong> risorse calcolate dall'analisi precedente
                {changeMaterialiInfo.hasOverride && <> (di cui alcune con <strong>override manuale</strong>)</>}.
              </div>
              <div className="p-3 rounded border border-amber-400 bg-amber-50/70 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-xs flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  Scegli come procedere. In tutti i casi gli override manuali vengono <strong>preservati</strong>.
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="ghost" onClick={() => setChangeMaterialiInfo(null)}
              data-testid="analisi-cambio-annulla">Annulla</Button>
            <Button variant="outline" onClick={() => _doApplyReal(null)}
              data-testid="analisi-cambio-mantieni">Mantieni esistenti</Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
              onClick={() => _doApplyReal("rigenera")}
              data-testid="analisi-cambio-rigenera">Rigenera dalla nuova</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RISORSE CALCOLATE (Fase C) — solo se c'è mapping attivo */}
      {attiva && (
        <RigaMaterialiCalcolatiPanel riga={riga} canEdit={canEdit}
          hasMappingAttivo={!!attiva} />
      )}

      {/* Dialog: SVILUPPO IN MATERIALI/MDO PREVISTA (Fase 1B) */}
      <Dialog open={bridgeOpen} onOpenChange={(o) => { if (!o) { setBridgeOpen(false); setBridgePlan(null); } }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="analisi-sviluppa-dialog">
          <DialogHeader>
            <DialogTitle>Sviluppa in Materiali / Manodopera prevista</DialogTitle>
            <div className="text-xs text-slate-500 mt-1">
              Trasferisce solo le risorse di tipo MATERIALE e MANODOPERA dell'analisi corrente
              nelle tabelle esistenti «Materiali previsti» e «Manodopera prevista».
              I record inseriti manualmente non vengono mai toccati.
              Nessuna scrittura in DB finché non confermi.
            </div>
          </DialogHeader>
          {bridgeLoading ? (
            <div className="text-slate-500 text-sm py-6 text-center">Caricamento preview…</div>
          ) : bridgePlan ? (
            <div className="space-y-3 text-sm">
              {(bridgePlan.warnings || []).length > 0 && (
                <div className="p-2 rounded border border-amber-400 bg-amber-50/70 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-xs space-y-1">
                  {bridgePlan.warnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-1">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {w}
                    </div>
                  ))}
                </div>
              )}

              <BridgeSection title="MATERIALI" section={bridgePlan.materiali} kind="mat" />
              <BridgeSection title="MANODOPERA (mode: ore)" section={bridgePlan.manodopera} kind="man" />

              {(bridgePlan.ignorate_altri_tipi || []).length > 0 && (
                <div className="p-3 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 text-xs">
                  <div className="uppercase tracking-widest font-semibold text-slate-500 mb-1">
                    Ignorate ({bridgePlan.ignorate_altri_tipi.length})
                  </div>
                  <div className="text-slate-600 dark:text-slate-300">
                    Non trasferite in questa fase (rimangono nell'Analisi Prezzi):
                    <ul className="list-disc list-inside mt-1">
                      {bridgePlan.ignorate_altri_tipi.map((i, k) => (
                        <li key={k}><Badge variant="outline" className="text-[10px] mr-1">{i.tipo_risorsa}</Badge>{i.descrizione}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="text-xs text-slate-500">
                Record manuali protetti: <strong>{bridgePlan.manuali_protetti?.materiali || 0}</strong> materiali,{" "}
                <strong>{bridgePlan.manuali_protetti?.manodopera || 0}</strong> manodopera. Nessuno di questi verrà toccato.
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setBridgeOpen(false); setBridgePlan(null); }}
              data-testid="analisi-sviluppa-annulla">Annulla</Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
              onClick={confirmBridge} disabled={bridgeLoading || bridgeApplying}
              data-testid="analisi-sviluppa-conferma">
              <Check className="h-4 w-4 mr-1" /> Conferma sviluppo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function BridgeSection({ title, section, kind }) {
  const daCreare = section?.da_creare || [];
  const daAgg = section?.da_aggiornare || [];
  const inv = section?.invariati || [];
  const empty = daCreare.length === 0 && daAgg.length === 0 && inv.length === 0;
  const fmt = (v) => v == null ? "—" : Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  const fmtEur = (v) => v == null ? "—" : `€ ${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
  return (
    <div className="border rounded overflow-hidden">
      <div className="px-3 py-2 bg-slate-50 dark:bg-slate-900/40 text-xs uppercase tracking-widest font-semibold flex flex-wrap gap-3 items-center">
        <span>{title}</span>
        <Badge className="bg-emerald-500 text-slate-950" data-testid={`bridge-${kind}-crea`}>Creare: {daCreare.length}</Badge>
        <Badge className="bg-amber-500 text-slate-950" data-testid={`bridge-${kind}-agg`}>Aggiornare: {daAgg.length}</Badge>
        <Badge variant="outline" data-testid={`bridge-${kind}-inv`}>Invariati: {inv.length}</Badge>
      </div>
      {empty ? (
        <div className="text-slate-500 text-xs px-3 py-2 italic">Nessuna risorsa di questo tipo nell'analisi.</div>
      ) : (
        <div className="max-h-64 overflow-y-auto text-xs">
          <table className="w-full">
            <thead className="text-[10px] uppercase tracking-widest text-slate-500 sticky top-0 bg-slate-50 dark:bg-slate-900/40">
              <tr>
                <th className="text-left px-2 py-1">Azione</th>
                <th className="text-left px-2 py-1">{kind === "man" ? "Tipo operaio" : "Descrizione"}</th>
                <th className="text-left px-2 py-1">UM</th>
                <th className="text-right px-2 py-1">{kind === "man" ? "Ore/UM" : "Coeff/UM"}</th>
                <th className="text-right px-2 py-1">{kind === "man" ? "Ore tot" : "Q.tà tot"}</th>
                <th className="text-right px-2 py-1">{kind === "man" ? "€/h" : "Prezzo"}</th>
                <th className="text-right px-2 py-1">Costo</th>
              </tr>
            </thead>
            <tbody>
              {[...daCreare.map(r => ({...r, _az: "Crea"})),
                ...daAgg.map(r => ({...r, _az: "Aggiorna"})),
                ...inv.map(r => ({...r, _az: "Invariato"}))].map((r, i) => {
                const qta = kind === "man" ? r.ore_previste : r.quantita_complessiva;
                const um = kind === "man" ? "h" : r.unita_misura;
                const coeff = kind === "man"
                  ? (r.ore_previste && r.ore_previste !== 0 ? Number(r.ore_previste) / (Number(r.quantita_complessiva) || 1) : r.coefficiente_per_unita)
                  : r.coefficiente_per_unita;
                // per manodopera in preview il coeff = incidenza (non presente esplicitamente): mostro ore_previste / qta computo se disponibile
                const nome = kind === "man" ? r.tipo_operaio : r.descrizione;
                return (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="px-2 py-1">
                      <span className={r._az === "Crea" ? "text-emerald-600 font-semibold" : r._az === "Aggiorna" ? "text-amber-600 font-semibold" : "text-slate-400"}>{r._az}</span>
                    </td>
                    <td className="px-2 py-1">{nome}</td>
                    <td className="px-2 py-1 font-mono">{um || "—"}</td>
                    <td className="px-2 py-1 text-right font-mono">{fmt(kind === "man" ? undefined : coeff)}</td>
                    <td className="px-2 py-1 text-right font-mono">{fmt(qta)}</td>
                    <td className="px-2 py-1 text-right font-mono">{fmtEur(kind === "man" ? r.costo_orario : r.prezzo_unitario)}</td>
                    <td className="px-2 py-1 text-right font-mono font-semibold">{fmtEur(r.costo_previsto)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function SuggestionCard({ s, onUse, disabled = false }) {
  const livColor = useMemo(() => {
    if (s.livello === "ALTO") return "bg-emerald-500 text-slate-950";
    if (s.livello === "MEDIO") return "bg-amber-500 text-slate-950";
    return "bg-rose-400 text-slate-950";
  }, [s.livello]);
  return (
    <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
         data-testid={`analisi-sug-${s.analisi_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 break-words">{s.descrizione}</div>
          <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
            {s.banca_nome && <span>{s.banca_nome}{s.banca_anno ? ` · ${s.banca_anno}` : ""}</span>}
            {s.codice_voce && <span>cod. {s.codice_voce}</span>}
            {s.unita_misura && <span>UM {s.unita_misura}</span>}
            {s.prezzo_totale != null && <span>€ {Number(s.prezzo_totale).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <Badge className={livColor} data-testid={`analisi-sug-livello-${s.analisi_id}`}>
            {s.livello} · {Number(s.score).toFixed(0)}%
          </Badge>
          {!disabled && (
            <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
              onClick={onUse} data-testid={`analisi-sug-usa-${s.analisi_id}`}>
              Usa questa analisi
            </Button>
          )}
        </div>
      </div>
      {(s.motivi?.length > 0 || s.differenze?.length > 0) && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          {s.motivi?.length > 0 && (
            <div>
              <div className="uppercase tracking-widest text-emerald-600 font-semibold mb-1">Motivi match</div>
              <ul className="list-disc list-inside space-y-0.5 text-slate-600 dark:text-slate-300">
                {s.motivi.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}
          {s.differenze?.length > 0 && (
            <div>
              <div className="uppercase tracking-widest text-rose-600 font-semibold mb-1">Differenze rilevate</div>
              <ul className="list-disc list-inside space-y-0.5 text-slate-600 dark:text-slate-300">
                {s.differenze.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
